import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BookingDialog from "@/components/BookingDialog";
import { fmtDateTime, parseISO } from "@/lib/dates";
import { toast } from "sonner";
import { CalendarClock, Pencil, Trash2 } from "lucide-react";

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [b, r] = await Promise.all([
        api.get("/bookings", { params: { mine: true } }),
        api.get("/rooms"),
      ]);
      setBookings(
        b.data.sort((x, y) => x.start.localeCompare(y.start)),
      );
      setRooms(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (b, series = false) => {
    const msg = series ? "Slet hele serien af gentagne bookinger?" : "Slet booking?";
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/bookings/${b.id}`, { params: { series } });
      toast.success(series ? "Serie slettet" : "Slettet");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const roomMap = Object.fromEntries(rooms.map((r) => [r.id, r]));
  const now = new Date();
  const upcoming = bookings.filter((b) => parseISO(b.end) > now);
  const past = bookings.filter((b) => parseISO(b.end) <= now);

  const renderBooking = (b) => {
    const room = roomMap[b.room_id];
    return (
      <Card
        key={b.id}
        className="p-5 rounded-2xl border-slate-200 hover:shadow-md transition-shadow"
        data-testid={`my-booking-${b.id}`}
      >
        <div className="flex items-start gap-4">
          <span
            className="w-1.5 self-stretch rounded-full"
            style={{ background: room?.color || "#94a3b8" }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display font-semibold text-lg">{b.title}</h3>
              {b.group_id && (
                <span className="text-[11px] px-2 py-0.5 bg-slate-100 rounded-full text-slate-600 inline-flex items-center gap-1">
                  ↻ Gentagende
                </span>
              )}
              {b.status === "pending" && <Badge variant="secondary">Afventer godkendelse</Badge>}
              {b.status === "cancelled" && <Badge variant="destructive">Annulleret</Badge>}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {room?.name || "—"} · {fmtDateTime(b.start)} → {fmtDateTime(b.end)}
            </p>
            {b.note && <p className="text-sm text-slate-600 mt-2">{b.note}</p>}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl"
              onClick={() => {
                setEditing(b);
                setOpen(true);
              }}
              data-testid={`edit-booking-${b.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl text-red-600 hover:text-red-700"
              onClick={() => handleDelete(b, false)}
              data-testid={`delete-booking-${b.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            {b.group_id && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl text-red-600 hover:text-red-700"
                onClick={() => handleDelete(b, true)}
                data-testid={`delete-series-${b.id}`}
                title="Slet hele serien"
              >
                <Trash2 className="h-4 w-4" />
                <span className="ml-1 text-[10px]">×serie</span>
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          Mine bookinger
        </h1>
        <p className="text-slate-500 mt-1">Overblik over dine kommende og tidligere bookinger</p>
      </div>

      <div>
        <h2 className="text-base md:text-lg font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <CalendarClock className="h-4 w-4" />
          Kommende ({upcoming.length})
        </h2>
        {upcoming.length === 0 ? (
          <Card className="p-8 rounded-2xl border-dashed text-center text-slate-500">
            Ingen kommende bookinger
          </Card>
        ) : (
          <div className="space-y-3">{upcoming.map(renderBooking)}</div>
        )}
      </div>

      {past.length > 0 && (
        <div>
          <h2 className="text-base md:text-lg font-semibold text-slate-500 mb-3">
            Tidligere ({past.length})
          </h2>
          <div className="space-y-3 opacity-70">{past.slice(-10).reverse().map(renderBooking)}</div>
        </div>
      )}

      {open && (
        <BookingDialog
          open={open}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
          onSaved={load}
          rooms={rooms.filter((r) => r.active)}
          initial={editing}
        />
      )}
    </div>
  );
}
